import { httpClient } from './http.client';

// Valid channel values accepted by the backend enum (case-insensitive, but
// "in-app" with a dash will NOT match — use "IN_APP" or "in_app" instead).
export type NotificationChannel = 'EMAIL' | 'SMS' | 'PUSH' | 'IN_APP' | 'WEBHOOK' | 'SLACK' | 'TEAMS';

const CHANNELS_KEY = 'ob_notification_channels';
const FALLBACK_CHANNELS: NotificationChannel[] = ['EMAIL'];

export function getDefaultChannels(): NotificationChannel[] {
  try {
    const stored = localStorage.getItem(CHANNELS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as NotificationChannel[];
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch { /* ignore */ }
  return FALLBACK_CHANNELS;
}

export function setDefaultChannels(channels: NotificationChannel[]): void {
  try {
    localStorage.setItem(CHANNELS_KEY, JSON.stringify(channels));
  } catch { /* ignore */ }
}

export interface SendNotificationPayload {
  recipients: string[];          // user IDs or email addresses
  title: string;
  message: string;
  notification_type?: string;
  action_url?: string;
  channels?: NotificationChannel[];
  priority?: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  metadata?: Record<string, unknown>;
}

/**
 * Fire-and-forget notification dispatch.
 * Silently logs failures — caller should never block on this.
 */
async function send(payload: SendNotificationPayload): Promise<void> {
  try {
    await httpClient.post('/api/v1/notifications/send', {
      recipients: payload.recipients,
      channels: payload.channels ?? getDefaultChannels(),
      title: payload.title,
      message: payload.message,
      notification_type: payload.notification_type ?? 'general',
      action_url: payload.action_url,
      priority: payload.priority ?? 'NORMAL',
      metadata: payload.metadata ?? {},
    });
  } catch (err) {
    console.error('[notification] send failed:', err);
  }
}

// ─── Event-specific helpers ────────────────────────────────────────────────

export const notificationService = {
  send,

  projectAssigned(pmUserId: string, projectName: string) {
    return send({
      recipients: [pmUserId],
      notification_type: 'project_assigned',
      action_url: '/projects',
      title: `You've been assigned as Project Manager`,
      message: `You have been selected as the Project Manager for "${projectName}". Log in to OptiBuild to review the project details and get started.`,
    });
  },

  taskCreated(ownerUserId: string, taskName: string, projectName: string) {
    return send({
      recipients: [ownerUserId],
      notification_type: 'task_assigned',
      action_url: '/schedules',
      title: `New task assigned: ${taskName}`,
      message: `A new task "${taskName}" has been assigned to you in project "${projectName}". Log in to view details and update your progress.`,
    });
  },

  dailyLogCreated(pmUserId: string, projectName: string, logDate: string) {
    return send({
      recipients: [pmUserId],
      notification_type: 'daily_log_submitted',
      action_url: '/daily-logs',
      title: `New daily log submitted for ${projectName}`,
      message: `A daily log for ${logDate} has been submitted for project "${projectName}". Log in to review.`,
    });
  },

  rfiCreated(assigneeUserId: string, rfiNumber: string, projectName: string) {
    return send({
      recipients: [assigneeUserId],
      notification_type: 'rfi_assigned',
      action_url: '/rfis',
      title: `RFI assigned to you: ${rfiNumber}`,
      message: `RFI "${rfiNumber}" in project "${projectName}" has been assigned to you. Please review and respond.`,
    });
  },

  submittalCreated(reviewerUserId: string, submittalNumber: string, projectName: string) {
    return send({
      recipients: [reviewerUserId],
      notification_type: 'submittal_review',
      action_url: '/submittals',
      title: `Submittal for review: ${submittalNumber}`,
      message: `Submittal "${submittalNumber}" in project "${projectName}" requires your review. Log in to OptiBuild to process it.`,
    });
  },

  punchlistItemCreated(assigneeUserId: string, itemTitle: string, projectName: string) {
    return send({
      recipients: [assigneeUserId],
      notification_type: 'punchlist_assigned',
      action_url: '/punch-lists',
      title: `Punch list item assigned: ${itemTitle}`,
      message: `A punch list item "${itemTitle}" in project "${projectName}" has been assigned to you. Please complete and mark it resolved.`,
    });
  },

  incidentCreated(investigatorUserId: string, incidentTitle: string, projectName: string) {
    return send({
      recipients: [investigatorUserId],
      notification_type: 'incident_assigned',
      action_url: '/safety-incidents',
      title: `Safety incident assigned for investigation`,
      message: `You have been assigned as the investigator for a safety incident "${incidentTitle}" in project "${projectName}". Please review and begin your investigation.`,
    });
  },

  poStatusChanged(requesterUserId: string, poNumber: string, newStatus: string) {
    return send({
      recipients: [requesterUserId],
      notification_type: 'po_status_changed',
      action_url: '/purchase-orders',
      title: `Purchase Order ${poNumber} status update`,
      message: `Your Purchase Order "${poNumber}" has been updated to status: ${newStatus}.`,
    });
  },

  grnCreated(poRequesterUserId: string, grnNumber: string, poNumber: string) {
    return send({
      recipients: [poRequesterUserId],
      notification_type: 'grn_created',
      action_url: '/goods-received',
      title: `Goods Received Note created for ${poNumber}`,
      message: `A Goods Received Note (${grnNumber}) has been recorded against your Purchase Order "${poNumber}". Log in to verify.`,
    });
  },

  materialRequisitionCreated(
    pmUserId: string,
    procurementUserIds: string[],
    requisitionNumber: string,
    projectName: string,
  ) {
    const recipients = [pmUserId, ...procurementUserIds].filter(Boolean);
    return send({
      recipients,
      notification_type: 'material_requisition',
      action_url: '/material-requisitions',
      title: `New material requisition: ${requisitionNumber}`,
      message: `A new material requisition "${requisitionNumber}" has been submitted for project "${projectName}". Please review and process.`,
    });
  },
};

export default notificationService;
